/**
 * VehicleSettingsSheet — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/05-flows.md § 11.1
 *
 * The CRUD-edit + delete entry point for a vehicle. Lives as a bottom sheet
 * triggered from the vehicle detail page's settings icon. Provides:
 *   - rename (custom label)
 *   - read-only display of immutable identifiers (VIN, plate, place ID)
 *   - link out to permissions management
 *   - hold-to-confirm delete with 7-day grace period banner (server-side)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Input } from '@/components/ui';
import { Plate, LTR } from '@/components/Bidi';
import { ApiError, updateVehicle, deleteVehicle, setDefaultVehicle, type VehicleSummary } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  vehicle: VehicleSummary;
  onSaved: (next: VehicleSummary) => void;
  onDeleted: () => void;
}

export function VehicleSettingsSheet({ open, onClose, vehicle, onSaved, onDeleted }: Props): JSX.Element {
  const { t } = useTranslation();
  const [label, setLabel] = useState(vehicle.customLabel ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    setError(null);
    setSaving(true);
    try {
      const next = await updateVehicle(vehicle.id, { customLabel: label.trim() || undefined });
      onSaved(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  async function onSetDefault() {
    setError(null);
    setSaving(true);
    try {
      const next = await setDefaultVehicle(vehicle.id);
      onSaved(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    setError(null);
    setSaving(true);
    try {
      await deleteVehicle(vehicle.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setSaving(false);
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('vehicle_settings.title', { defaultValue: 'הגדרות הרכב' })}
      footer={
        <Button variant="primary" size="md" fullWidth disabled={saving} onClick={onSave}>
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      }
    >
      <div className="space-y-5">
        <Input
          name="customLabel"
          label={t('vehicle_settings.label_label', { defaultValue: 'תווית אישית' })}
          placeholder={t('vehicle_settings.label_placeholder', { defaultValue: 'למשל: רכב משפחתי' })}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={64}
        />

        {/* Read-only immutable identifiers */}
        <div className="rounded-md bg-carnaf-paper-elevated p-3 space-y-2">
          <Row label="VIN" value={<LTR>{vehicle.vin}</LTR>} />
          {vehicle.licensePlate && (
            <Row label={t('vehicle.plate', { defaultValue: 'לוחית' })} value={<Plate>{vehicle.licensePlate}</Plate>} />
          )}
          <Row label="Place ID" value={<LTR>{vehicle.placeId}</LTR>} />
          <Row label={t('vehicle.year', { defaultValue: 'שנה' })} value={String(vehicle.year)} />
        </div>

        {/* Default car */}
        <button
          type="button"
          onClick={vehicle.isDefault ? undefined : onSetDefault}
          disabled={saving || vehicle.isDefault}
          className="w-full flex items-center justify-between py-3 border-t border-carnaf-line text-body text-carnaf-ink text-start disabled:opacity-100"
        >
          <span>{t('vehicle_settings.default_car', { defaultValue: 'רכב ברירת מחדל' })}</span>
          <span className="text-carnaf-teal text-body-sm font-semibold">
            {vehicle.isDefault
              ? t('vehicle_settings.is_default', { defaultValue: 'ברירת מחדל ✓' })
              : t('vehicle_settings.set_default', { defaultValue: 'הגדרה כברירת מחדל' })}
          </span>
        </button>

        {/* Permissions link */}
        <Link
          to={`/vehicles/${vehicle.id}/permissions`}
          onClick={onClose}
          className="flex items-center justify-between py-3 border-t border-b border-carnaf-line text-body text-carnaf-ink"
        >
          <span>{t('vehicle_settings.manage_access', { defaultValue: 'ניהול גישות' })}</span>
          <span className="text-carnaf-ink-faint">‹</span>
        </Link>

        {error && (
          <p role="alert" className="text-body-sm text-carnaf-red">
            {error}
          </p>
        )}

        {/* Delete — hold-to-confirm */}
        <div className="pt-4 border-t border-carnaf-line">
          <Button variant="danger" size="md" fullWidth holdMs={1500} onHoldConfirm={onDelete} disabled={saving}>
            {t('vehicle_settings.delete_hold', {
              defaultValue: 'מחיקת הרכב (החזיקו 1.5ש׳)',
            })}
          </Button>
          <p className="mt-2 text-caption text-carnaf-ink-muted text-center leading-relaxed">
            {t('vehicle_settings.delete_helper', {
              defaultValue: '7 ימים לשחזור. כל המסמכים והאירועים יוסרו.',
            })}
          </p>
        </div>
      </div>
    </BottomSheet>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between items-baseline text-body-sm">
      <span className="text-carnaf-ink-muted">{label}</span>
      <span className="text-carnaf-ink font-medium">{value}</span>
    </div>
  );
}
